<template>
  <div class="proposal-generator">
    <div class="generator-header">
      <button @click="$emit('back')" class="back-btn">
        ← Back to Tenants
      </button>
      
      <div class="tenant-info">
        <h2>🤖 Generate Schedule for {{ tenant.name }}</h2>
        <p>Create an AI-powered schedule proposal</p>
      </div>
    </div>

    <!-- Generation Form -->
    <div class="generation-form" v-if="!isGenerating && !generatedProposal">
      <div class="form-section">
        <h3>📅 Schedule Period</h3>
        
        <div class="form-grid">
          <div class="form-group">
            <label>Start Date</label>
            <input
              v-model="formData.startDate"
              type="date"
              required
              :min="today"
              class="form-input"
            >
          </div>
          
          <div class="form-group">
            <label>End Date</label>
            <input
              v-model="formData.endDate"
              type="date"
              required
              :min="formData.startDate || today"
              class="form-input"
            >
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3>⚙️ Generation Options</h3>
        
        <div class="form-grid">
          <div class="form-group">
            <label>Generation Method</label>
            <select v-model="formData.method" class="form-input">
              <option value="constraint_solver">🎯 Constraint Solver - Optimization algorithm (RECOMMENDED)</option>
              <option value="claude">🤖 Claude AI - Direct generation</option>
              <option value="ml_model">📊 ML Model - Learns from your reviews</option>
              <option value="rule_engine">⚙️ Rule Engine - Basic round-robin scheduling</option>
            </select>
            <small class="form-help">Constraint Solver uses Tabu Search optimization to satisfy all constraints and optimize schedules</small>
          </div>
          
          <div class="form-group">
            <label>Optimization Goal</label>
            <select v-model="formData.optimizationGoal" class="form-input">
              <option value="employee_satisfaction">Employee Satisfaction</option>
              <option value="cost_efficiency">Cost Efficiency</option>
              <option value="coverage_optimization">Coverage Optimization</option>
              <option value="balanced">Balanced Approach</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label>
            <input
              v-model="formData.respectPreferences"
              type="checkbox"
              class="form-checkbox"
            >
            Respect Employee Preferences
          </label>
        </div>

        <div class="form-group">
          <label>
            <input
              v-model="formData.avoidConflicts"
              type="checkbox"
              class="form-checkbox"
            >
            Avoid Schedule Conflicts
          </label>
        </div>

        <div class="form-group">
          <label>
            <input
              v-model="formData.balanceWorkload"
              type="checkbox"
              class="form-checkbox"
            >
            Balance Workload Distribution
          </label>
        </div>

        <div class="form-group">
          <label>
            <input
              v-model="formData.interactiveMode"
              type="checkbox"
              class="form-checkbox"
            >
            🎬 Interactive Mode - Watch schedule generation in real-time
          </label>
          <small class="form-help">Shows live calendar grid as shifts are assigned (like Timefold solver)</small>
        </div>
      </div>

      <div class="form-section">
        <h3>🎯 Schedule Constraints</h3>
        
        <div class="constraints-form-grid">
          <div class="form-group">
            <label>Working Hours Start</label>
            <input
              v-model="formData.constraints.workingHoursStart"
              type="time"
              class="form-input"
            >
          </div>
          
          <div class="form-group">
            <label>Working Hours End</label>
            <input
              v-model="formData.constraints.workingHoursEnd"
              type="time"
              class="form-input"
            >
          </div>
          
          <div class="form-group">
            <label>Target Workers Per Hour</label>
            <input
              v-model.number="formData.constraints.targetWorkersPerHour"
              type="number"
              min="1"
              max="20"
              class="form-input"
              placeholder="4"
            >
            <small class="form-help">How many workers should be working each hour (shifts can overlap)</small>
          </div>
          
          <div class="form-group">
            <label>Max Hours per Week</label>
            <input
              v-model.number="formData.constraints.maxHoursPerWeek"
              type="number"
              min="1"
              max="80"
              class="form-input"
              placeholder="40"
            >
          </div>
          
          <div class="form-group">
            <label>Min Rest Between Shifts (hours)</label>
            <input
              v-model.number="formData.constraints.minRestBetweenShifts"
              type="number"
              min="1"
              max="48"
              class="form-input"
              placeholder="12"
            >
          </div>
        </div>
        
        <div class="constraints-preview">
          <h4>📋 Constraint Summary:</h4>
          <div class="constraint-preview-items">
            <span class="constraint-preview-item">
              🕒 Working Hours: {{ formData.constraints.workingHoursStart || '09:00' }} - {{ formData.constraints.workingHoursEnd || '17:00' }}
            </span>
            <span class="constraint-preview-item">
              👥 Target Workers/Hour: {{ formData.constraints.targetWorkersPerHour || 4 }}
            </span>
            <span class="constraint-preview-item">
              ⏱️ Max Hours/Week: {{ formData.constraints.maxHoursPerWeek || 40 }}h
            </span>
            <span class="constraint-preview-item">
              😴 Min Rest: {{ formData.constraints.minRestBetweenShifts || 12 }}h
            </span>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button
          @click="generateProposal"
          :disabled="!isFormValid"
          class="generate-btn"
        >
          🤖 Generate AI Schedule
        </button>
        
        <button @click="$emit('back')" class="cancel-btn">
          Cancel
        </button>
      </div>
    </div>

    <!-- Interactive Schedule Viewer -->
    <div v-if="showInteractiveViewer" class="interactive-viewer-container">
      <div class="interactive-viewer-header">
        <h2>🎬 Live Schedule Generation</h2>
        <button @click="cancelGeneration" class="stop-btn">
          ⏹️ Stop Generation
        </button>
      </div>
      
      <InteractiveScheduleViewer
        ref="interactiveViewer"
        :targetWorkers="formData.constraints.targetWorkersPerHour"
        :startDate="formData.startDate"
        :endDate="formData.endDate"
        @complete="onInteractiveComplete"
        @error="onInteractiveError"
      />
    </div>

    <!-- Generation Progress -->
    <div class="generation-progress" v-if="isGenerating && !showInteractiveViewer">
      <div class="progress-header">
        <h3>🤖 AI is generating your schedule...</h3>
        <p>{{ currentStep }}</p>
      </div>

      <div class="progress-bar-container">
        <div class="progress-bar">
          <div
            class="progress-fill"
            :style="{ width: `${progress}%` }"
          ></div>
        </div>
        <div class="progress-percentage">{{ Math.round(progress) }}%</div>
      </div>

      <div class="generation-details">
        <div class="detail-item">
          <span class="detail-label">Tenant:</span>
          <span class="detail-value">{{ tenant.name }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Period:</span>
          <span class="detail-value">{{ formData.startDate }} to {{ formData.endDate }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Method:</span>
          <span class="detail-value">{{ getMethodLabel(formData.method) }}</span>
        </div>
      </div>

      <button @click="cancelGeneration" class="cancel-generation-btn">
        Cancel Generation
      </button>
    </div>

    <!-- Generation Error -->
    <div class="generation-error" v-if="generationError">
      <div class="error-icon">❌</div>
      <h3>Generation Failed</h3>
      <p>{{ generationError }}</p>
      <div class="error-actions">
        <button @click="retryGeneration" class="retry-btn">
          Try Again
        </button>
        <button @click="clearError" class="cancel-btn">
          Go Back
        </button>
      </div>
    </div>

    <!-- Generated Proposal Preview -->
    <div class="proposal-preview" v-if="generatedProposal && !isGenerating">
      <div class="preview-header">
        <h3>✅ Schedule Generated Successfully!</h3>
        <p>Review the proposed schedule below</p>
      </div>

      <div class="proposal-stats">
        <div class="stat-card">
          <div class="stat-number">{{ generatedProposal.shift_count || 0 }}</div>
          <div class="stat-label">Shifts Created</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-number">{{ generatedProposal.employee_count || 0 }}</div>
          <div class="stat-label">Employees Scheduled</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-number">{{ Math.round((generatedProposal.confidence_score || 0) * 100) }}%</div>
          <div class="stat-label">Confidence Score</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-number">{{ conflictCount }}</div>
          <div class="stat-label">Conflicts Detected</div>
        </div>
      </div>

      <div class="proposal-summary" v-if="generatedProposal.summary">
        <h4>📋 Summary</h4>
        <p>{{ generatedProposal.summary }}</p>
      </div>

      <!-- Calendar Grid Visualization -->
      <div class="calendar-grid" v-if="scheduleDates.length > 0">
        <h4>📅 Schedule Calendar</h4>
        <div class="calendar-week">
          <div
            v-for="dateStr in scheduleDates"
            :key="dateStr"
            class="calendar-day"
          >
            <div class="day-header">
              <div class="day-name">{{ getDayName(dateStr) }}</div>
              <div class="day-date">{{ formatDayDate(dateStr) }}</div>
            </div>
            <div class="day-shifts">
              <div
                v-for="(shift, idx) in shiftsByDate[dateStr]"
                :key="idx"
                class="calendar-shift"
                :class="{ 'is-dummy': shift.is_dummy }"
              >
                <div class="shift-name">{{ shift.employee_name }}</div>
                <div class="shift-time">{{ shift.start_time }}-{{ shift.end_time }}</div>
              </div>
              <div v-if="!shiftsByDate[dateStr] || shiftsByDate[dateStr].length === 0" class="no-shifts">
                No shifts
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Basic Shifts List -->
      <div class="shifts-preview" v-if="proposalShifts.length > 0">
        <h4>👥 Shifts Overview (First 10)</h4>
        <div class="shifts-list">
          <div
            v-for="shift in proposalShifts.slice(0, 10)"
            :key="shift.id"
            class="shift-item"
          >
            <div class="shift-employee">{{ shift.employee_name }}</div>
            <div class="shift-date">{{ formatDate(shift.date) }}</div>
            <div class="shift-time">{{ shift.start_time }} - {{ shift.end_time }}</div>
            <div class="shift-role">{{ shift.role || 'Worker' }}</div>
          </div>
        </div>
        
        <p v-if="proposalShifts.length > 10" class="more-shifts">
          ... and {{ proposalShifts.length - 10 }} more shifts
        </p>
      </div>

      <div class="proposal-actions">
        <button @click="reviewProposal" class="review-btn">
          📋 Review & Edit Schedule
        </button>
        
        <button @click="approveProposal" class="approve-btn">
          ✅ Approve & Deploy
        </button>
        
        <button @click="startOver" class="start-over-btn">
          🔄 Generate New Schedule
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useAISchedulerStore } from '../../stores/aiScheduler.js'
import aiSchedulerApi from '../../services/aiSchedulerApi.js'
import InteractiveScheduleViewer from './InteractiveScheduleViewer.vue'

const API_BASE_URL = import.meta.env.DEV 
  ? 'http://192.168.0.200:8000'
  : ''  // Use relative URL in production (goes through nginx)

const props = defineProps({
  tenant: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['back', 'proposal-generated', 'review-proposal', 'proposal-approved'])

const aiSchedulerStore = useAISchedulerStore()

// Form data
const formData = ref({
  startDate: '',
  endDate: '',
  method: 'constraint_solver',  // Default to Constraint Solver (optimization)
  optimizationGoal: 'employee_satisfaction',
  respectPreferences: true,
  avoidConflicts: true,
  balanceWorkload: true,
  interactiveMode: false,  // Interactive real-time visualization
  constraints: {
    workingHoursStart: '09:00',
    workingHoursEnd: '17:00',
    maxHoursPerWeek: 40,
    minRestBetweenShifts: 12,
    targetWorkersPerHour: 4  // Default to 4 workers per hour
  }
})

// State
const constraints = ref(null)
const generatedProposal = ref(null)
const interactiveViewer = ref(null)
const showInteractiveViewer = ref(false)

// Computed properties
const today = computed(() => {
  const date = new Date()
  return date.toISOString().split('T')[0]
})

const isGenerating = computed(() => aiSchedulerStore.generation.isGenerating)
const progress = computed(() => aiSchedulerStore.generation.progress)
const currentStep = computed(() => aiSchedulerStore.generation.currentStep)
const generationError = computed(() => aiSchedulerStore.generation.error)

const isFormValid = computed(() => {
  return formData.value.startDate && 
         formData.value.endDate && 
         formData.value.startDate <= formData.value.endDate
})

const proposalShifts = computed(() => {
  // Backend returns schedule as array of employee schedules
  // Each employee has a shifts array
  
  const employeeSchedules = generatedProposal.value?.proposal_data?.shifts || []
  
  
  // Flatten to get all shifts across all employees
  const allShifts = []
  employeeSchedules.forEach((empSchedule, idx) => {
    const employeeShifts = empSchedule.shifts || []
    
    employeeShifts.forEach(shift => {
      allShifts.push({
        ...shift,
        employee_id: empSchedule.employee_id,
        employee_name: empSchedule.employee_name,
        is_dummy: empSchedule.is_dummy || false
      })
    })
  })
  
  return allShifts
})

// Group shifts by date for calendar display
const shiftsByDate = computed(() => {
  const grouped = {}
  proposalShifts.value.forEach(shift => {
    if (!grouped[shift.date]) {
      grouped[shift.date] = []
    }
    grouped[shift.date].push(shift)
  })
  return grouped
})

// Get dates in the schedule period for calendar grid
const scheduleDates = computed(() => {
  if (!formData.value.startDate || !formData.value.endDate) return []
  
  const dates = []
  const start = new Date(formData.value.startDate)
  const end = new Date(formData.value.endDate)
  
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(new Date(d).toISOString().split('T')[0])
  }
  
  return dates
})

const conflictCount = computed(() => {
  return generatedProposal.value?.proposal_data?.conflicts?.length || 0
})

// Methods
const generateProposal = async () => {
  try {
    // If interactive mode is enabled, show live viewer
    if (formData.value.interactiveMode) {
      showInteractiveViewer.value = true
      
      // Build SSE URL for streaming
      const schemaName = props.tenant.schema_name || props.tenant.id
      
      // Build request body (same as normal generation)
      const requestData = {
        employee_ids: [], // Will fetch all active employees
        time_period: {
          start_date: formData.value.startDate,
          end_date: formData.value.endDate
        },
        constraints: {
          operating_hours: `${formData.value.constraints.workingHoursStart}-${formData.value.constraints.workingHoursEnd}`,
          max_weekly_hours: formData.value.constraints.maxHoursPerWeek,
          min_rest_hours: formData.value.constraints.minRestBetweenShifts,
          target_workers_per_hour: formData.value.constraints.targetWorkersPerHour,
          min_staff: formData.value.constraints.targetWorkersPerHour
        },
        generation_method: formData.value.method
      }
      
      // Build SSE URL with query params
      const params = new URLSearchParams()
      params.append('tenant', schemaName)
      params.append('data', JSON.stringify(requestData))
      
      // Get service token (EventSource can't send custom headers)
      const serviceAuthStore = (await import('../../stores/serviceAuth.js')).useServiceAuthStore()
      const token = serviceAuthStore.token
      if (token) {
        params.append('token', token)
      }
      
      const sseUrl = `${API_BASE_URL}/api/schedule/generate-stream/?${params.toString()}`
      
      // Start interactive generation
      await new Promise((resolve) => {
        setTimeout(() => {
          if (interactiveViewer.value) {
            interactiveViewer.value.startGeneration(sseUrl)
          }
          resolve()
        }, 100)
      })
      
    } else {
      // Normal batch generation
      const proposal = await aiSchedulerStore.generateProposal(
        props.tenant.schema_name || props.tenant.id,
        formData.value.startDate,
        formData.value.endDate,
        {
          method: formData.value.method,
          optimization_goal: formData.value.optimizationGoal,
          respect_preferences: formData.value.respectPreferences,
          avoid_conflicts: formData.value.avoidConflicts,
          balance_workload: formData.value.balanceWorkload,
          custom_constraints: {
            working_hours: {
              start: formData.value.constraints.workingHoursStart,
              end: formData.value.constraints.workingHoursEnd
            },
            max_hours_per_week: formData.value.constraints.maxHoursPerWeek,
            min_rest_between_shifts: formData.value.constraints.minRestBetweenShifts,
            target_workers_per_hour: formData.value.constraints.targetWorkersPerHour
          }
        }
      )
      
      generatedProposal.value = proposal
      emit('proposal-generated', proposal)
    }
  } catch (error) {
    console.error('Failed to generate proposal:', error)
  }
}

const cancelGeneration = () => {
  if (showInteractiveViewer.value && interactiveViewer.value) {
    interactiveViewer.value.stopGeneration()
  }
  aiSchedulerStore.generation.isGenerating = false
  aiSchedulerStore.clearGenerationError()
  showInteractiveViewer.value = false
}

const onInteractiveComplete = (schedule) => {
  console.log('Interactive generation complete:', schedule)
  showInteractiveViewer.value = false
  
  // Convert to proposal format
  generatedProposal.value = {
    proposal_data: schedule,
    time_period: {
      start_date: formData.value.startDate,
      end_date: formData.value.endDate
    }
  }
  
  emit('proposal-generated', generatedProposal.value)
}

const onInteractiveError = (error) => {
  console.error('Interactive generation error:', error)
  showInteractiveViewer.value = false
}

const retryGeneration = () => {
  aiSchedulerStore.clearGenerationError()
  generateProposal()
}

const clearError = () => {
  aiSchedulerStore.clearGenerationError()
}

const reviewProposal = () => {
  emit('review-proposal', generatedProposal.value)
}

const approveProposal = async () => {
  try {
    await aiSchedulerStore.approveProposal(
      generatedProposal.value.id,
      'Approved via AI Scheduler Frontend'
    )
    emit('proposal-approved', generatedProposal.value)
  } catch (error) {
    console.error('Failed to approve proposal:', error)
  }
}

const startOver = () => {
  generatedProposal.value = null
  aiSchedulerStore.clearGenerationError()
}

const loadConstraints = async () => {
  try {
    constraints.value = await aiSchedulerApi.getTenantConstraints(
      props.tenant.schema_name || props.tenant.id
    )
    
    // Update form data with loaded constraints
    if (constraints.value) {
      formData.value.constraints = {
        workingHoursStart: constraints.value.working_hours?.start || '09:00',
        workingHoursEnd: constraints.value.working_hours?.end || '17:00',
        maxHoursPerWeek: constraints.value.max_hours_per_week || 40,
        minRestBetweenShifts: constraints.value.min_rest_between_shifts || 12
      }
    }
  } catch (error) {
    console.warn('Failed to load tenant constraints:', error)
  }
}

const getMethodLabel = (method) => {
  const labels = {
    'claude': '🤖 Claude AI',
    'ml_model': '📊 ML Model',
    'rule_engine': '⚙️ Rule Engine'
  }
  return labels[method] || method
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  }).format(date)
}

const getDayName = (dateStr) => {
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)
}

const formatDayDate = (dateStr) => {
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat('en-US', { month: 'numeric', day: 'numeric' }).format(date)
}

// Set default dates (next week)
const setDefaultDates = () => {
  const now = new Date()
  const nextWeek = new Date(now)
  nextWeek.setDate(now.getDate() + 7)
  
  // Start of next week (Monday)
  const startOfWeek = new Date(nextWeek)
  const day = startOfWeek.getDay()
  const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1)
  startOfWeek.setDate(diff)
  
  // End of next week (Friday)
  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 6)
  
  formData.value.startDate = startOfWeek.toISOString().split('T')[0]
  formData.value.endDate = endOfWeek.toISOString().split('T')[0]
}

// Lifecycle
onMounted(async () => {
  setDefaultDates()
  await loadConstraints()
})

// Watch for prop changes
watch(() => props.tenant, async () => {
  if (props.tenant) {
    await loadConstraints()
  }
}, { immediate: true })
</script>

<style scoped>
.proposal-generator {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.generator-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.back-btn {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #e5e7eb;
}

.tenant-info h2 {
  margin: 0;
  color: #1a1a1a;
  font-size: 1.5rem;
  font-weight: 600;
}

.tenant-info p {
  margin: 0.25rem 0 0 0;
  color: #6b7280;
}

.generation-form {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 2rem;
}

.form-section {
  margin-bottom: 2rem;
}

.form-section h3 {
  margin: 0 0 1rem 0;
  color: #1a1a1a;
  font-size: 1.125rem;
  font-weight: 600;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.form-input {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-help {
  display: block;
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-style: italic;
}

.form-checkbox {
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
}

.constraints-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.constraints-preview {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 0.75rem;
  padding: 1.5rem;
}

.constraints-preview h4 {
  margin: 0 0 1rem 0;
  color: #0c4a6e;
  font-size: 1rem;
  font-weight: 600;
}

.constraint-preview-items {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.constraint-preview-item {
  background: white;
  color: #0c4a6e;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #bae6fd;
  font-size: 0.875rem;
  font-weight: 500;
}

.constraints-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.constraint-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.375rem;
}

.constraint-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.constraint-value {
  color: #1a1a1a;
  font-weight: 500;
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.generate-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.generate-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
}

.generation-progress {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 2rem;
  text-align: center;
}

.progress-header h3 {
  margin: 0 0 0.5rem 0;
  color: #1a1a1a;
  font-size: 1.25rem;
}

.progress-header p {
  margin: 0 0 2rem 0;
  color: #6b7280;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.progress-bar {
  flex: 1;
  height: 0.5rem;
  background: #e5e7eb;
  border-radius: 0.25rem;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
  transition: width 0.3s ease;
}

.progress-percentage {
  font-weight: 600;
  color: #3b82f6;
  min-width: 3rem;
}

.generation-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.375rem;
}

.detail-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.detail-value {
  color: #1a1a1a;
  font-weight: 500;
  font-size: 0.875rem;
}

.cancel-generation-btn {
  padding: 0.5rem 1rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.generation-error {
  background: white;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  padding: 2rem;
  text-align: center;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.generation-error h3 {
  margin: 0 0 0.5rem 0;
  color: #dc2626;
}

.generation-error p {
  margin: 0 0 2rem 0;
  color: #6b7280;
}

.error-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.retry-btn {
  padding: 0.75rem 1.5rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
}

.proposal-preview {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 2rem;
}

.preview-header {
  text-align: center;
  margin-bottom: 2rem;
}

.preview-header h3 {
  margin: 0 0 0.5rem 0;
  color: #10b981;
  font-size: 1.25rem;
}

.proposal-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.proposal-summary {
  margin-bottom: 2rem;
}

.proposal-summary h4 {
  margin: 0 0 0.5rem 0;
  color: #1a1a1a;
}

.shifts-preview {
  margin-bottom: 2rem;
}

.shifts-preview h4 {
  margin: 0 0 1rem 0;
  color: #1a1a1a;
}

.shifts-list {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.shift-item {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.shift-employee {
  font-weight: 500;
  color: #1a1a1a;
}

.shift-date, .shift-time, .shift-role {
  color: #6b7280;
}

.more-shifts {
  color: #6b7280;
  font-style: italic;
  text-align: center;
}

.proposal-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.review-btn, .approve-btn, .start-over-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.review-btn {
  background: #f3f4f6;
  color: #374151;
}

.review-btn:hover {
  background: #e5e7eb;
}

.approve-btn {
  background: #10b981;
  color: white;
}

.approve-btn:hover {
  background: #059669;
}

.start-over-btn {
  background: #6b7280;
  color: white;
}

.start-over-btn:hover {
  background: #4b5563;
}

/* Calendar Grid Styles */
.calendar-grid {
  margin-bottom: 2rem;
}

.calendar-grid h4 {
  margin: 0 0 1rem 0;
  color: #1a1a1a;
}

.calendar-week {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.5rem;
  overflow-x: auto;
}

.calendar-day {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  flex-direction: column;
}

.day-header {
  background: #f9fafb;
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.day-name {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.day-date {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.day-shifts {
  padding: 0.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.calendar-shift {
  background: #dbeafe;
  border: 1px solid #93c5fd;
  border-left: 3px solid #3b82f6;
  border-radius: 0.375rem;
  padding: 0.5rem;
  font-size: 0.75rem;
}

.calendar-shift.is-dummy {
  background: #fef3c7;
  border-color: #fbbf24;
  border-left-color: #f59e0b;
}

.shift-name {
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.calendar-shift.is-dummy .shift-name {
  color: #92400e;
}

.shift-time {
  color: #64748b;
  font-size: 0.7rem;
}

.no-shifts {
  color: #9ca3af;
  font-style: italic;
  font-size: 0.75rem;
  text-align: center;
  padding: 2rem 0.5rem;
}

/* Interactive Viewer Styles */
.interactive-viewer-container {
  margin-top: 2rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.interactive-viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.interactive-viewer-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1f2937;
}

.stop-btn {
  padding: 0.625rem 1.25rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem;
}

.stop-btn:hover {
  background: #dc2626;
  transform: scale(1.05);
}

.stop-btn:active {
  transform: scale(0.95);
}
</style>